/**
 * Demo reports — the demo book counted over a stretch of time.
 *
 * The bookings screen answers "who is coming"; the follow-up list answers
 * "what do I owe somebody". This one answers "how did the month go", which
 * is a different question and wants a different shape: whole periods, one
 * number each, and last period beside this one so a figure means something.
 *
 * ## Whole periods, stepped through
 *
 * A report is asked for by the day, the week, the month or the year, and
 * almost always for one that has finished. So the period is a grain plus an
 * offset - daily at -1 is yesterday, monthly at -1 is last month - and the
 * arrows step it. A custom pair of dates covers everything else. Nothing
 * here takes "the last 30 days" as a period: a month that ends on a Tuesday
 * afternoon cannot be compared with the one before it.
 *
 * ## What is counted
 *
 * Demos are counted by when they were due to happen, in Dubai. Completed,
 * no-show and cancelled come from Calendly; registered and not registered
 * come from the match to the Client Master; under follow-up is what the
 * demo team handed to sales and sales has not closed.
 *
 * Demos and people are counted differently and labelled as such - four
 * sessions with one partner are four demos and one partner, and a report
 * that blurs the two is a report nobody can act on.
 */
import { useEffect, useMemo, useState } from 'react';
import { useApp } from '../store/AppContext.jsx';
import { shiftSummary, rungLabel, RUNGS } from '../domain/demoShift.js';
import api from '../lib/api.js';
import Icon from '../lib/icons.jsx';
import { download } from '../lib/format.js';
import { Card, CardBody, EmptyState, Avatar, Pill, Spinner } from '../components/Primitives.jsx';
import { RankBars } from '../components/SalesCharts.jsx';
import { PeriodBars, SplitBar } from '../components/DemoCharts.jsx';
import TileDetail from '../components/modals/TileDetail.jsx';
import {
  TZ_LABEL, stamp, dayText, hhmm, stateOf, STATE_META, followState, FOLLOW_META, CLOSED_META,
  REGISTERED, REG_META, atDubai, addDays, periodRange, trendBuckets
} from '../domain/demos.js';

/* A due date without the year, the way the bookings page writes it. Its
   own copy rather than an import: that page keeps the helper private,
   and one line is cheaper than widening its surface. */
const shortDate = iso => dayText(`${iso}T12:00:00+04:00`).replace(/ \d{4}$/, '');

const GRAINS = [
  ['day', 'Daily'],
  ['week', 'Weekly'],
  ['month', 'Monthly'],
  ['year', 'Yearly'],
  ['custom', 'Custom']
];

/* The two kinds of customer are two identities, so they get two hues rather
   than two steps of one. Both pairs are validated against their own surface
   - the light cyan does not clear 3:1 on white, which is why every segment
   carries its number in ink rather than leaving the colour to say it. */
const B2B_COLOR = 'var(--sp-b2b)';
const B2C_COLOR = 'var(--sp-b2c)';

/** Who a booking belongs to: the client, else the contact, else the email. */
const whoKey = b => {
  const s = b.session || {};
  return String(s.clientId || s.contactId || String(b.invitee.email || '').toLowerCase() || b.id);
};

/**
 * Everything the report states, for one window.
 *
 * One pass, because the same booking feeds a dozen of the numbers and
 * walking the book once per tile is how a report starts taking a second to
 * open.
 */
function summarise(bookings, from, to, now) {
  const lo = atDubai(from);
  const hi = atDubai(to);
  const rows = bookings.filter(b => {
    const t = new Date(b.start).getTime();
    return t >= lo && t < hi;
  });

  const m = {
    scheduled: 0, completed: 0, noShow: 0, cancelled: 0, upcoming: 0, rescheduled: 0,
    b2bDemos: 0, b2cDemos: 0, matchedDemos: 0, manualDemos: 0, unmatchedDemos: 0,
    byHand: 0, fromCalendly: 0,
    handed: 0, following: 0, closed: 0, notHanded: 0
  };
  /* Keyed by person, so "users" is people and "demos" is demos. */
  const people = new Map();

  rows.forEach(b => {
    const s = b.session || {};
    const st = stateOf(b, now);
    m.scheduled += 1;
    if (st === 'done') m.completed += 1;
    else if (st === 'noshow') m.noShow += 1;
    else if (st === 'canceled') m.cancelled += 1;
    else m.upcoming += 1;
    if (b.rescheduled || b.rescheduledFrom) m.rescheduled += 1;

    const partner = s.customerType === 'B2B';
    if (partner) m.b2bDemos += 1; else m.b2cDemos += 1;

    /* Where the booking came from. Every figure on this page is a
       Calendly booking unless it says otherwise, so how many are not is
       worth a line of its own - a month with twelve demos entered by
       hand is a month Calendly's own numbers will not agree with. */
    if (s.isManual) m.byHand += 1; else m.fromCalendly += 1;

    const how = REGISTERED(b);
    if (how === 'matched') m.matchedDemos += 1;
    else if (how === 'manual') m.manualDemos += 1;
    else m.unmatchedDemos += 1;

    const fs = b.session ? followState(b) : 'open';
    if (fs === 'handed') m.handed += 1;
    else if (fs === 'following') m.following += 1;
    else if (fs === 'closed') m.closed += 1;
    else m.notHanded += 1;

    const key = whoKey(b);
    let p = people.get(key);
    if (!p) {
      p = {
        key,
        name: (b.client && b.client.name) || (b.contact && b.contact.name) || b.invitee.name || b.invitee.email || 'Unknown',
        code: (b.client && b.client.code) || (b.contact && b.contact.code) || '',
        how,
        partner,
        business: s.business || '',
        email: b.invitee.email || '',
        phone: b.invitee.phone || '',
        sessions: 0, completed: 0, noShow: 0, cancelled: 0, chasing: false, last: null,
        /* The sessions themselves, so a row can be opened and read rather
           than only counted. */
        demos: []
      };
      people.set(key, p);
    }
    p.sessions += 1;
    if (p.how === 'none') p.how = how;
    p.partner = p.partner || partner;
    if (st === 'done') p.completed += 1;
    if (st === 'noshow') p.noShow += 1;
    if (st === 'canceled') p.cancelled += 1;
    if (fs === 'handed' || fs === 'following') p.chasing = true;
    if (!p.last || new Date(b.start) > new Date(p.last)) p.last = b.start;
    p.email = p.email || b.invitee.email || '';
    p.phone = p.phone || b.invitee.phone || '';
    p.business = p.business || s.business || '';
    p.demos.push({
      id: b.id,
      start: b.start,
      end: b.end,
      meeting: b.eventTypeName || b.name || '',
      state: st,
      host: (b.host && b.host.name) || '',
      attendees: (s.attendees || []).map(a => a.name),
      follow: fs,
      owner: s.ownerName || '',
      handedBy: s.handedBy || '',
      note: s.handedNote || '',
      closedReason: s.closedReason || '',
      feedback: s.feedback || []
    });
  });

  /*
   * What happened, crossed with whether they were already a customer.
   *
   * A person, once, not a booking. Counted per demo, somebody who booked
   * three times was in it three times - a no-show, a cancellation and a
   * completion for one person - and the block read as three people who
   * did three different things. The sides came to 181 while the demos
   * came from 147 people, and nobody could tell which question they were
   * looking at.
   *
   * Which of their outcomes stands for them is the whole question, and
   * the best one does: somebody who missed a demo and came to the next is
   * a person who attended a demo, and counting them as a no-show because
   * they also missed one would be the wrong answer to "how many of these
   * people did we actually get in front of".
   */
  const BEST = { done: 4, live: 3, upcoming: 3, noshow: 2, canceled: 1 };
  m.split = [...people.values()].reduce((acc, p) => {
    const best = p.demos.reduce(
      (win, d) => ((BEST[d.state] || 0) > (BEST[win] || 0) ? d.state : win), '');
    if (!best) return acc;
    const key = p.how === 'none' ? 'not' : 'reg';
    acc[best] = acc[best] || { reg: 0, not: 0 };
    acc[best][key] += 1;
    return acc;
  }, {});

  const all = [...people.values()];
  m.people = all.length;
  /* Registered is registered, however it was established. */
  m.registeredUsers = all.filter(p => p.how !== 'none').length;
  m.unregisteredUsers = all.filter(p => p.how === 'none').length;
  m.underFollowUp = all.filter(p => p.chasing).length;
  /* Who came back, and how many demos that accounts for - the gap
     between "162 demos" and "135 people" in one figure. */
  const again = all.filter(p => p.sessions > 1);
  m.repeatPeople = again.length;
  m.repeatDemos = again.reduce((n, p) => n + p.sessions, 0);
  m.partners = all.filter(p => p.partner).length;
  /* "with number of sessions if multiple" - the partners who came back. */
  m.repeatPartners = all.filter(p => p.partner && p.sessions > 1).length;
  m.partnerList = all.filter(p => p.partner)
    .map(p => ({ ...p, demos: p.demos.slice().sort((x, y) => new Date(x.start) - new Date(y.start)) }))
    .sort((a, b) => b.sessions - a.sessions || String(a.name).localeCompare(b.name));
  /* Everyone, not only the partners: a tile that counts people has to be
     able to show which people, and the counts above are cuts of this. */
  m.peopleList = all
    .map(p => ({ ...p, demos: p.demos.slice().sort((x, y) => new Date(x.start) - new Date(y.start)) }))
    .sort((a, b) => b.sessions - a.sessions || String(a.name).localeCompare(b.name));
  m.rows = rows;
  return m;
}

/*
 * The three places a follow-up can be, as three lists.
 *
 * The bar above says how many are in each; this says which. They are
 * three sections and not one list with a column, because they are three
 * different questions asked by three different people on three
 * different days — what is sitting untouched, what is being worked, and
 * what came of the ones that are done — and a single list sorted by
 * anything at all answers none of them.
 *
 * Read-only, like the rest of the report. The work is on the booking.
 */
const FOLLOW_SECTIONS = [
  ['handed', 'Not started', 'alert', 'g-amber',
    'Handed to sales, and nobody has picked it up yet.',
    'Nothing is waiting to be picked up',
    'Hand-over note'],
  ['following', 'Currently following up', 'flag', 'g-blue',
    'Somebody is chasing these now.',
    'Nobody is chasing anything from this period',
    'What is next'],
  ['closed', 'Closed follow-ups', 'check', 'g-slate',
    'Finished with, and how each one ended.',
    'Nothing has been closed from this period',
    'How it ended']
];

function FollowSection({ kind, title, icon, tone, note, empty, third, list, now }) {
  return (
    <Card>
      <CardBody>
        <header className="rphead">
          <div><h4>{title}</h4><p>{note}</p></div>
          <span className="rpn">{list.length}</span>
        </header>

        {!list.length ? (
          <EmptyState icon={icon} tone={tone} title={empty} />
        ) : (
          <>
            {/* The third column says a different thing in each section,
                so the columns are named rather than left to be worked
                out from the first row. */}
            <div className="rpfhead">
              <span>When</span>
              <span>Who</span>
              <span>{third}</span>
              <span>Status</span>
            </div>
            <ol className="rpfollows">
            {list.map(b => {
              const d = b.session || {};
              const [sl, stone, sicon] = STATE_META[stateOf(b, now)];
              const code = (b.client && b.client.code) || (b.contact && b.contact.code) || '';
              /* Only on the closed list, and only where somebody said
                 which — the older rows were filed under "other". */
              const ended = kind === 'closed' && d.closedAs && d.closedAs !== 'other'
                ? CLOSED_META[d.closedAs] : null;
              const overdue = kind === 'following' && d.nextDue
                && Date.parse(`${d.nextDue}T23:59:59+04:00`) < now;

              return (
                <li key={b.id}>
                  <span className="rpfwhen">
                    <b>{dayText(b.start)}</b>
                    <small>{hhmm(b.start)}</small>
                  </span>

                  <div className="rpfwho">
                    <b>{b.invitee.name || b.invitee.email || 'Unknown invitee'}</b>
                    <small>
                      {code ? <span className="dbk-code">{code}</span> : null}
                      {b.invitee.email || '—'}
                    </small>
                  </div>

                  <div className="rpfwhat">
                    {kind === 'closed' ? (
                      <>
                        {/* "Closed with Demo rescheduled" — the word
                            closed is what the section is about, and the
                            category on its own read as a thing that had
                            just happened rather than how it finished. */}
                        <b>Closed{ended ? ` with ${ended[0]}` : ''}</b>
                        {d.closedReason ? <small>{d.closedReason}</small> : <small className="rpnone">No reason was written</small>}
                      </>
                    ) : kind === 'following' ? (
                      <>
                        <b>{d.nextAction || 'No next action written'}</b>
                        {d.nextDue
                          ? <small className={overdue ? 'bad' : ''}>{overdue ? 'overdue ' : 'by '}{shortDate(d.nextDue)}</small>
                          : <small className="rpnone">No date set</small>}
                      </>
                    ) : (
                      <>
                        <b>{d.handedBy ? `Handed over by ${d.handedBy}` : 'Handed to sales'}</b>
                        {d.handedNote
                          ? <small>&ldquo;{d.handedNote}&rdquo;</small>
                          : <small className="rpnone">No note was written</small>}
                      </>
                    )}
                  </div>

                  <div className="rpftags">
                    {ended ? (
                      <Pill tone={ended[1]} icon={ended[2]}>Closed with {ended[0].toLowerCase()}</Pill>
                    ) : null}
                    <Pill tone={stone} icon={sicon}>{sl}</Pill>
                  </div>
                </li>
              );
            })}
            </ol>
          </>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * A partner, and the sessions behind the number.
 *
 * The counts answer "is anybody coming back"; opening the row answers
 * "what happened in each one" - which meeting, whether it ran, who took
 * it, and what was written afterwards. Read-only, like the rest of the
 * report: the work is done on the booking.
 */
function PartnerRow({ p, open, onToggle, children }) {
  return (
    <>
      <tr className={`rppartner${open ? ' on' : ''}`} onClick={onToggle}>
        <td className="rpopen">
          <button type="button" className={`rpchev${open ? ' on' : ''}`} aria-expanded={open}
            title={open ? 'Hide the sessions' : `Show all ${p.sessions} session${p.sessions === 1 ? '' : 's'}`}>
            <Icon name="chev" />
          </button>
        </td>
        {children}
      </tr>

      {open ? (
        <tr className="rpdetailrow">
          <td colSpan={7}>
            <div className="rpdetail">
              <dl className="rpfacts">
                <div><dt>Email</dt><dd>{p.email || '—'}</dd></div>
                <div><dt>Phone</dt><dd>{p.phone || '—'}</dd></div>
                <div><dt>Business</dt><dd>{p.business || '—'}</dd></div>
                <div><dt>Client ID</dt><dd>{p.code || 'not on the books'}</dd></div>
              </dl>

              <ol className="rpsessions">
                {p.demos.map((d, i) => {
                  const [label, tone, icon] = STATE_META[d.state];
                  const fm = FOLLOW_META[d.follow];
                  return (
                    <li key={d.id}>
                      <span className="rpno">{i + 1}</span>
                      <div className="rpsbody">
                        <div className="rpshead">
                          <b>{dayText(d.start)}, {hhmm(d.start)}</b>
                          <span>{d.meeting}{d.host ? ` · with ${d.host}` : ''}</span>
                          <Pill tone={tone} icon={icon}>{label}</Pill>
                          {fm ? <Pill tone={fm[1]} icon={fm[2]} className="dbk-client">{fm[0]}</Pill> : null}
                        </div>

                        <div className="rpsmeta">
                          <span>
                            <i>Taken by</i>
                            {d.attendees.length ? d.attendees.join(', ') : 'not recorded'}
                          </span>
                          {d.owner ? <span><i>Owner</i>{d.owner}</span> : null}
                          {d.handedBy ? <span><i>Handed over by</i>{d.handedBy}</span> : null}
                        </div>

                        {d.note ? (
                          <p className="rpquote"><i>From the demo team:</i> {d.note}</p>
                        ) : null}
                        {d.closedReason ? (
                          <p className="rpquote closed"><i>Closed:</i> {d.closedReason}</p>
                        ) : null}
                        {d.feedback.length ? (
                          <ul className="rpfb">
                            {d.feedback.map((f, k) => (
                              <li key={k}><p>{f.text}</p><small>{f.by || 'system'} · {stamp(f.at)}</small></li>
                            ))}
                          </ul>
                        ) : null}
                        {!d.note && !d.closedReason && !d.feedback.length ? (
                          <p className="rpnone">Nothing was written about this session.</p>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

/** A rung's colour, from the ladder itself so the two cannot drift. */
const tone = key => (RUNGS.find(r => r.key === key) || RUNGS[0]).tone;

/**
 * Before and after, on one pair of rails.
 *
 * This was three separate splits side by side, and reading a change out of
 * two charts means holding one in your head while you look at the other.
 * One card, two bars on the same scale, and the shift is where the join
 * moves: the eye does the subtraction.
 *
 * The bars are not stacked on top of each other either - overlaid bars
 * read as one quantity, and these are two states of the same people.
 */
function ShiftCompare({ shift }) {
  const held = shift.held || 0;
  const share = n => (held ? (n / held) * 100 : 0);

  const rails = [
    { when: 'On the day', yes: shift.reg.beforeYes.length, no: shift.reg.beforeNo.length },
    { when: 'Now', yes: shift.reg.afterYes.length, no: shift.reg.afterNo.length }
  ];

  const plan = [
    { label: 'Upgraded', value: shift.plan.up.length, tone: 'up' },
    { label: 'Downgraded', value: shift.plan.down.length, tone: 'down' },
    { label: 'No change', value: shift.plan.same.length, tone: 'flat' },
    { label: 'Not recorded', value: shift.plan.unknown.length, tone: 'unknown' }
  ];

  return (
    <div className="dscmp">
      <header>
        <h5>Registered, before the demo and now</h5>
        {/* Named once, above both rails: the two bars use the same two
            colours, so saying it twice would only take up room. */}
        <ul className="dskey">
          <li><i className="reg" />Registered</li>
          <li><i className="unreg" />Not registered</li>
        </ul>
      </header>

      <div className="dsrails">
        {rails.map(r => (
          <div className="dsrail" key={r.when}>
            <span className="dswhen">{r.when}</span>
            <div className="dstrack">
              <div className="dsseg reg" style={{ width: `${share(r.yes)}%` }}>
                {share(r.yes) > 9 ? <b>{r.yes}</b> : null}
              </div>
              <div className="dsseg unreg" style={{ width: `${share(r.no)}%` }}>
                {share(r.no) > 9 ? <b>{r.no}</b> : null}
              </div>
            </div>
            <span className="dspair">
              <b>{r.yes}</b> registered <em>·</em> <b>{r.no}</b> not
            </span>
          </div>
        ))}
      </div>

      {/* The one number the whole card exists to produce. */}
      <p className={`dsmoved${shift.reg.became.length ? '' : ' none'}`}>
        {shift.reg.became.length
          ? <>
            <b>+{shift.reg.became.length}</b> of the {held} who attended had no account
            on the day and have one now.
          </>
          : <>Nobody who attended has registered since.</>}
      </p>

      <div className="dsway">
        <h5>
          Plan movement
          <em>of the {shift.reg.beforeYes.length} who were already registered</em>
        </h5>
        <ul>
          {plan.map(p => (
            <li key={p.label} className={p.tone}>
              <i />
              <span>{p.label}</span>
              <b>{p.value}</b>
            </li>
          ))}
        </ul>
        {shift.plan.unknown.length ? (
          <p className="dsfoot">
            {shift.plan.unknown.length} of them were clients before this report began
            recording the plan on the day, so an upgrade among those is invisible
            rather than absent.
          </p>
        ) : null}
      </div>
    </div>
  );
}

/*
 * The state each tile counts, so it can ask cur.split for the same cut.
 *
 * Only the four that count demos: a tile counting people has no
 * registered/not split to show, because registered is what it is
 * already splitting on.
 */
/*
 * Which states each tile counts, so it can ask cur.split for the same
 * cut. Upcoming takes two, because the tile's own count does: a demo
 * running right now has not happened yet either.
 *
 * Only the tiles that count demos. One that counts people has no
 * registered split to show - registered is what it is already splitting
 * on.
 */
const TILE_STATE = {
  completed: ['done'],
  noshow: ['noshow'],
  cancelled: ['canceled'],
  upcoming: ['upcoming', 'live']
};

/**
 * One figure, and the rows behind it.
 *
 * A button, not a card: it opens something, and somebody on a keyboard
 * has to be able to reach it.
 */
function TileCard({ t, onOpen }) {
  return (
    <button
      type="button"
      className={`rptile ${t.c}`}
      onClick={() => onOpen(t.k)}
      title={`Show the ${t.n} behind this`}
    >
      <div className={`ic ${t.c}`}><Icon name={t.i} solid /></div>
      <div className="rpn">{t.n}</div>
      <div className="rpl">{t.l}</div>
      <div className="rpsub">{t.s}</div>
      <Icon name="chev" className="rptgo" />
    </button>
  );
}

/*
 * The four states, in the order a demo passes through them.
 *
 * Upcoming takes two: a demo running right now has not happened yet
 * either, and the tile above counts it the same way.
 */
const SPLIT_ROWS = [
  { k: 'done', label: 'Completed', states: ['done'], cell: 'done' },
  { k: 'noshow', label: 'No-show', states: ['noshow'], cell: 'noshow' },
  { k: 'canceled', label: 'Cancelled', states: ['canceled'], cell: 'canceled' },
  { k: 'upcoming', label: 'Still to come', states: ['upcoming', 'live'], cell: 'upcoming' }
];

/**
 * One side of the register, as its own panel.
 *
 * Registered and not registered are two groups of people, so they get a
 * panel each rather than two columns of one table: side by side at the
 * same scale, the shape of one is read against the shape of the other
 * without anybody reading a number.
 *
 * A person each, so the two panels together come to the people who
 * booked rather than to the demos they booked. Somebody who came three
 * times sits in one row, under the best of the three - counted per demo
 * they were a no-show, a cancellation and a completion at once, which
 * read as three people doing three different things.
 */
function SplitPanel({ side, total, rows, all, onOpen }) {
  const registered = side === 'reg';
  return (
    <section className={`rpside ${registered ? 'reg' : 'unreg'}`}>
      {/* The headline opens the whole side, with a tab per outcome; a
          row opens the same sheet on that tab. One sheet either way,
          because whoever reads the completed ones wants the no-shows
          next and should not have to close anything to get there. */}
      <button type="button" className="rphdr" onClick={() => onOpen(side)}>
        <div className="rpstop">
          <b>{total}</b>
          <span>{registered ? 'Registered' : 'Not registered'}</span>
          <em>{all ? Math.round((total / all) * 100) : 0}%</em>
        </div>
        <p>
          {registered
            ? 'Demos with people who already had an account.'
            : 'Demos with people we had nothing on.'}
          <span className="rpsee">See all {total}<Icon name="chev" /></span>
        </p>
      </button>
      <ul>
        {rows.map(r => {
          const n = r[side];
          return (
            <li key={r.k}>
              <span className="rpsl">{r.label}</span>
              <div className="rpsw">
                <i style={{ width: `${total ? (n / total) * 100 : 0}%` }} />
              </div>
              {n
                ? (
                  <button type="button" className="rpsn" onClick={() => onOpen(`${r.cell}:${side}`)}>
                    {n}
                  </button>
                )
                : <span className="rpsn off">0</span>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * The people who came back.
 *
 * It was a tile reading "28", which says how many but not how often, and
 * a tile has no room to say it. The gap between 164 demos and 135 people
 * is entirely this, and it is worth a card: how many came twice, how many
 * came more, and who came most.
 *
 * Counted over the period on screen. Somebody who had one demo in August
 * and one in September came once in September.
 */
function CameBack({ cur, onOpen }) {
  const again = (cur.peopleList || []).filter(p => p.sessions > 1);
  if (!again.length) return null;

  /* Two, three, four or more. Past four the buckets are one person each
     and say less than the list underneath does. */
  const bands = [
    { k: 2, label: 'twice', ring: 'var(--brand)', n: again.filter(p => p.sessions === 2).length },
    { k: 3, label: 'three times', ring: 'var(--brand2)', n: again.filter(p => p.sessions === 3).length },
    { k: 4, label: 'four or more', ring: 'var(--sp-up)', n: again.filter(p => p.sessions >= 4).length }
  ].filter(b => b.n);

  return (
    <div className="rpback">
      <header className="rphead">
        <div>
          <h4>Came more than once</h4>
          <p>
            <b>{cur.repeatPeople}</b> of the {cur.people} people who booked a demo booked
            more than one — <b>{cur.repeatDemos}</b> demos between them. That is the whole
            gap between {cur.scheduled} demos and {cur.people} people.
          </p>
        </div>
        <button type="button" className="rpall" onClick={onOpen}>
          See all {cur.repeatPeople}<Icon name="chev" />
        </button>
      </header>

      <div className="rpbands">
        {bands.map(b => (
          <div className="rpband" key={b.k}>
            {/* A ring, not a bar: one of these is 27 and another is 1,
                and a bar four percent long reads as an empty bar. */}
            <div
              className="rpring"
              style={{ '--pc': Math.max(5, Math.round((b.n / again.length) * 100)), '--ring': b.ring }}
            >
              <b>{b.n}</b>
            </div>
            <div>
              <span>came {b.label}</span>
              <small>{Math.round((b.n / again.length) * 100)}% of those who came back</small>
            </div>
          </div>
        ))}
      </div>

      {/* The handful who came back most, because a number cannot be
          telephoned and a name can. */}
      <ol className="rpwho">
        {again.slice(0, 5).map(p => (
          <li key={p.key}>
            <Avatar name={p.name} />
            <div>
              <b>{p.name}</b>
              <small>{p.email || '—'}{p.business ? ` · ${p.business}` : ''}</small>
            </div>
            <em>{p.sessions} demos</em>
            <span className="rpwhon">
              {p.completed ? `${p.completed} held` : 'none held'}
              {p.noShow ? ` · ${p.noShow} no-show` : ''}
            </span>
          </li>
        ))}
      </ol>
      {again.length > 5 ? (
        <p className="rpmore">
          <button type="button" onClick={onOpen}>and {again.length - 5} more</button>
        </p>
      ) : null}
    </div>
  );
}

export default function DemoReports() {
  const { S, demos, rows, setSheet, loadDemos, toast } = useApp();
  const [grain, setGrain] = useState('month');
  const [offset, setOffset] = useState(0);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [saving, setSaving] = useState('');
  /* Which figure is open. A tile is a number and nothing else until you
     can see the rows behind it. */
  const [tile, setTile] = useState('');
  /* Which partner's sessions are open. */
  const [openPartner, setOpenPartner] = useState(null);

  /* A different period is a different set of partners, so an open one folds
     rather than showing the sessions of whoever held that row before. */
  useEffect(() => { setOpenPartner(null); }, [grain, offset, from, to]);

  const data = demos;
  const loading = data === null;
  const all = data?.bookings || [];
  const now = Date.now();

  useEffect(() => { if (demos === null) loadDemos(); }, [demos, loadDemos]);

  /*
   * The plan ladder, so "upgraded" is not a guess.
   *
   * Price decides, and only price. The obvious tie-break - where two plans
   * cost the same, take the order they are listed in settings - looks like
   * a ladder and is not one: that list also carries PROFESSIONAL, which
   * XYRA retired and seventeen accounts are still sitting on. Ranking by
   * position would call a move onto a retired plan an upgrade.
   *
   * So a plan priced the same as another cannot be ordered against it, and
   * a plan missing from the list cannot be ordered at all. Both come back
   * as not known, which is a different answer from "no change" and is
   * reported as one.
   */
  const planRank = useMemo(() => {
    const prices = S.prices || {};
    return plan => {
      const p = prices[String(plan || '').trim().toUpperCase()];
      return typeof p === 'number' ? p : null;
    };
  }, [S.prices]);

  const custom = { from, to };
  const period = useMemo(() => periodRange(grain, offset, custom), [grain, offset, from, to]);
  const cur = useMemo(() => summarise(all, period.from, period.to, now), [all, period, now]);

  /*
   * Before and after, for the demos in this period. Kept to the period
   * the rest of the page is showing, so the tiles above and this agree
   * about which demos are being talked about.
   */
  /*
   * The demos behind each follow-up state, newest first — see
   * FollowSection. Counted off the same rows the bar above counts, so
   * the three lists and the three bars cannot disagree.
   */
  const follows = useMemo(() => {
    const out = { handed: [], following: [], closed: [] };
    (cur.rows || []).forEach(b => {
      const k = followState(b);
      if (out[k]) out[k].push(b);
    });
    Object.values(out).forEach(l => l.sort((a, b) => new Date(b.start) - new Date(a.start)));
    return out;
  }, [cur]);

  const shift = useMemo(() => {
    const lo = atDubai(period.from);
    const hi = atDubai(period.to);
    const within = all.filter(b => {
      const t = new Date(b.start).getTime();
      return t >= lo && t < hi;
    });
    return shiftSummary(rows, within, undefined, planRank);
  }, [all, rows, period, planRank]);

  const trend = useMemo(() => {
    const { buckets, title } = trendBuckets(period, custom);
    return {
      title,
      points: buckets.map(x => ({
        key: x.key,
        label: x.label,
        now: !!x.now,
        value: all.filter(b => {
          const t = new Date(b.start).getTime();
          return t >= atDubai(x.from) && t < atDubai(x.to);
        }).length
      }))
    };
  }, [all, period, from, to]);

  const step = n => setOffset(o => o + n);
  const rate = (n, of) => (of ? `${Math.round((n / of) * 100)}%` : '—');
  const happened = cur.completed + cur.noShow;

  /*
   * The crossing, shaped for the two panels.
   *
   * Each row carries both sides so the panels can be drawn from one
   * list — two lists would be two things to keep in step, and the whole
   * point of the card is that the two sides are the same demos counted
   * once.
   */
  const splitRows = SPLIT_ROWS
    .map(r => {
      const c = r.states.reduce((acc, k) => {
        const x = cur.split[k] || { reg: 0, not: 0 };
        return { reg: acc.reg + x.reg, not: acc.not + x.not };
      }, { reg: 0, not: 0 });
      return { ...r, reg: c.reg, not: c.not };
    })
    .filter(r => r.reg || r.not);

  const splitTotals = splitRows.reduce(
    (t, r) => ({ reg: t.reg + r.reg, not: t.not + r.not, all: t.all + r.reg + r.not }),
    { reg: 0, not: 0, all: 0 }
  );

  const tiles = [
    { k: 'scheduled', l: 'Demos scheduled', n: cur.scheduled, i: 'cal', c: 'g-blue', s: 'booked into this period' },
    { k: 'completed', l: 'Completed', n: cur.completed, i: 'check', c: 'g-green', s: 'the demo happened' },
    { k: 'noshow', l: 'No-shows', n: cur.noShow, i: 'eyeoff', c: 'g-amber', s: 'they did not turn up' },
    { k: 'cancelled', l: 'Cancelled', n: cur.cancelled, i: 'x', c: 'g-red', s: 'called off before the day' },
    /* Upcoming had no tile of its own, which was fine while the tiles were
       only counts - the other three plus this one is the scheduled figure
       and it could be inferred. Now that each state carries its own
       registered split, an inferred state has nowhere to carry one. */
    { k: 'upcoming', l: 'Still to come', n: cur.upcoming, i: 'clock', c: 'g-pink', s: 'the slot has not passed yet' },
    /* Registered and not registered had a tile each, counting people
       while everything around them counted demos - so they did not add
       up to anything on the screen and were the first thing queried.
       The card directly below says it per demo, split by what happened,
       and reconciles with the figures up here. */
    { k: 'follow', l: 'Under follow-up', n: cur.underFollowUp, i: 'flag', c: 'g-orange', s: `of ${cur.people} people, with sales` },
    { k: 'b2b', l: 'B2B sessions', n: cur.b2bDemos, i: 'building', c: 'g-purple', s: `${cur.partners} partner${cur.partners === 1 ? '' : 's'}${cur.repeatPartners ? `, ${cur.repeatPartners} with more than one` : ''}` }
  ];

  /**
   * The report as a spreadsheet, laid out by the server.
   *
   * What goes up is what is on screen - the same figures, the breakdowns
   * behind them, the partners, and every booking the period contains - so
   * the file and the page can never disagree.
   */
  const payload = () => ({
    title: 'XYRA — demo report',
    timezone: TZ_LABEL,
    generatedAt: stamp(Date.now()),
    period: { label: period.label, sub: period.sub || '', from: period.from, to: period.to },
    /* The first eight are the tiles, in the order they are on screen, so
       the file and the page read the same way round. */
    headline: [
      ...tiles.map(t => ({ measure: t.l, value: t.n, note: t.s })),
      { measure: 'Still upcoming', value: cur.upcoming, note: 'not yet had their slot' },
      { measure: 'Rescheduled', value: cur.rescheduled, note: 'moved on Calendly' },
      { measure: 'People who booked', value: cur.people, note: 'distinct people' },
      { measure: 'B2B partners', value: cur.partners, note: 'with at least one session' },
      { measure: 'B2C demos', value: cur.b2cDemos, note: 'customer demos' }
    ],
    /*
     * The crossing, sent as its own block rather than as a breakdown.
     *
     * A breakdown is one measure split one way; this is two axes, and
     * flattening it into "Completed — registered" rows would lose the
     * thing that makes it readable, which is that it adds up in both
     * directions.
     */
    split: { rows: splitRows.map(r => ({ label: r.label, reg: r.reg, not: r.not, total: r.reg + r.not })) },
    breakdowns: [
      {
        title: 'What came of them',
        what: 'Outcome',
        note: happened ? `${rate(cur.completed, happened)} of the demos that reached their slot were attended.` : '',
        total: cur.scheduled,
        rows: [
          { label: 'Completed', value: cur.completed },
          { label: 'No-show', value: cur.noShow },
          { label: 'Cancelled', value: cur.cancelled },
          { label: 'Still upcoming', value: cur.upcoming }
        ]
      },
      {
        title: 'Who booked them',
        what: 'Booked by',
        note: `${cur.people} ${cur.people === 1 ? 'person' : 'people'} booked ${cur.scheduled} demo${cur.scheduled === 1 ? '' : 's'}.`,
        total: cur.scheduled,
        rows: [
          { label: 'Registered', value: cur.matchedDemos },
          { label: 'Registered manually', value: cur.manualDemos },
          { label: 'Not registered', value: cur.unmatchedDemos }
        ]
      },
      {
        title: 'How they were booked',
        what: 'Booked through',
        note: cur.byHand
          ? `${cur.byHand} of ${cur.scheduled} never went through Calendly - they were arranged directly and entered by hand.`
          : 'Every demo in this period came through Calendly.',
        total: cur.scheduled,
        rows: [
          { label: 'Booked on Calendly', value: cur.fromCalendly },
          { label: 'Added by hand', value: cur.byHand }
        ]
      },
      {
        title: 'Partners and customers',
        what: 'Customer type',
        note: 'Taken from the meeting that was booked on Calendly.',
        total: cur.scheduled,
        rows: [
          { label: 'B2B partner sessions', value: cur.b2bDemos },
          { label: 'B2C customer demos', value: cur.b2cDemos }
        ]
      },
      {
        title: 'Where the follow-ups got to',
        what: 'Follow-up',
        total: cur.scheduled,
        rows: [
          { label: 'Not started', value: cur.handed },
          { label: 'Following up', value: cur.following },
          { label: 'Closed', value: cur.closed },
          { label: 'Not handed over', value: cur.notHanded }
        ]
      }
    ],
    partners: cur.partnerList.map(p => ({
      name: p.name, code: p.code, business: p.business, sessions: p.sessions,
      completed: p.completed, noShow: p.noShow,
      registered: p.how !== 'none', registeredHow: REG_META[p.how][0],
      chasing: p.chasing, last: p.last ? stamp(p.last) : ''
    })),
    /* The per-demo detail is not sent: the server has those rows already,
       and a month of them is a couple of hundred kilobytes of JSON for a
       request whose whole job is to format a file. */

    /*
     * The before-and-after does go up, because the server cannot work it
     * out: it is read against the clients app, which the browser already
     * has loaded and the export route does not. Flattened to what a cell
     * can hold, with every "not known" written out rather than left
     * blank — a blank in a spreadsheet reads as "no change".
     */
    shift: {
      held: shift.held,
      /*
       * The three counts the block leads with, sent as counts rather than
       * left for the server to re-derive from the rows. The page and the
       * file then cannot disagree, which is the whole reason the rows go
       * up in the first place.
       */
      counts: {
        regBefore: shift.reg.beforeYes.length,
        unregBefore: shift.reg.beforeNo.length,
        regAfter: shift.reg.afterYes.length,
        unregAfter: shift.reg.afterNo.length,
        became: shift.reg.became.length,
        planUp: shift.plan.up.length,
        planDown: shift.plan.down.length,
        planSame: shift.plan.same.length,
        planUnknown: shift.plan.unknown.length
      },
      rows: shift.all.map(x => ({
        name: x.client?.name || x.booking.invitee?.name || x.booking.invitee?.email || 'Unknown',
        email: x.booking.invitee?.email || '',
        demo: stamp(x.booking.start),
        before: rungLabel(x.before),
        after: rungLabel(x.after),
        planBefore: x.planBefore
          || (x.before === 'none' ? 'no plan' : 'not recorded'),
        planNow: x.plan || 'no plan',
        /* "No change" is a claim, and on a row with no plan recorded on the
           day it is one nothing here can make: the rung held, and whether
           the plan moved underneath it is not known. Saying so keeps the
           three apart — nothing happened, we cannot tell, and it moved —
           which one word for all three had quietly run together. */
        changed: x.moved > 0
          ? (x.planMoved ? 'Moved up, and changed plan' : 'Moved up')
          : x.moved < 0 ? 'Lapsed since'
            : x.planMoved ? 'Changed plan'
              : (x.before !== 'none' && !x.planBefore) ? 'Plan not recorded'
                : 'No change',
        moved: x.moved,
        planMoved: x.planMoved,
        said: x.said ? (x.saidWhy ? `${x.said} — ${x.saidWhy}` : x.said) : '',
        registered: stamp(x.client && (x.client.reg_on || x.client.regOn)),
        lastPaid: stamp(x.client && (x.client.last_paid || x.client.lastPaid))
      }))
    }
  });

  /* Both files come from the same payload, so the workbook and the CSV can
     never show different numbers - or different numbers from the page. */
  const save = async kind => {
    setSaving(kind);
    try {
      const report = payload();
      const blob = kind === 'xlsx' ? await api.calendly.reportBook(report)
        : kind === 'pdf' ? await api.calendly.reportPdf(report)
          : await api.calendly.reportCsv(report);
      download(blob, `demo-report-${period.from}.${kind}`);
      toast(`Exported the ${period.label} report`);
    } catch (e) {
      toast(e.message);
    } finally {
      setSaving('');
    }
  };

  if (data && data.connected === false) {
    return (
      <Card>
        <CardBody>
          <EmptyState icon="chart" tone="g-cyan" title="Calendly is not connected">
            The report counts the demo bookings. Connect Calendly in <code>backend/.env</code> and it fills in.
          </EmptyState>
        </CardBody>
      </Card>
    );
  }


  return (
    <div className="rp">
      {/* Paper has no sidebar to say which workspace this came from and no
          period control to say what is being counted, so the print sheet
          carries its own heading. Hidden on screen. */}
      <header className="rpprint">
        <div>
          <b>XYRA — demo report</b>
          <span>{period.label}{period.sub ? ` · ${period.sub}` : ''}</span>
        </div>
        <div>
          <span>{period.from} to {addDays(period.to, -1)}</span>
          <span>All times {TZ_LABEL} · printed {stamp(Date.now())}</span>
        </div>
      </header>

      {/* -------------------------------------------------- the period */}
      <div className="rpbar">
        <div className="rpgrain" role="tablist" aria-label="Report period">
          {GRAINS.map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={grain === k}
              className={`rpg${grain === k ? ' on' : ''}`}
              onClick={() => { setGrain(k); setOffset(0); }}
            >
              {label}
            </button>
          ))}
        </div>

        {grain === 'custom' ? (
          <div className="rpdates">
            <label>
              <span>From</span>
              <input type="date" value={from} max={to || undefined} onChange={e => setFrom(e.target.value)} />
            </label>
            <label>
              <span>To</span>
              <input type="date" value={to} min={from || undefined} onChange={e => setTo(e.target.value)} />
            </label>
            {/* Without this the only way back out of a half-typed range was
                to leave the tab and come back; clearing drops both dates and
                the report falls back to today. */}
            <button
              type="button"
              className="rpclear"
              onClick={() => { setFrom(''); setTo(''); }}
              disabled={!from && !to}
              title={from || to ? 'Clear both dates' : 'No dates to clear'}
            >
              <Icon name="x" />Clear
            </button>
            <div className="rpwhen tight">
              <b>{period.label}</b>
              <small>{period.sub}</small>
            </div>
          </div>
        ) : (
          <div className="rpstep">
            <button type="button" className="rpnav back" onClick={() => step(-1)} title="The period before this one">
              <Icon name="chev" />
            </button>
            <div className="rpwhen">
              <span className="rpwi"><Icon name="cal" solid /></span>
              <div>
                <b>{period.label}</b>
                <small>{period.sub}</small>
              </div>
            </div>
            <button
              type="button"
              className="rpnav fwd"
              onClick={() => step(1)}
              disabled={offset >= 0}
              title={offset >= 0 ? 'This is the current period' : 'The period after this one'}
            >
              <Icon name="chev" />
            </button>
          </div>
        )}
        {grain !== 'custom' && offset !== 0 ? (
          <button type="button" className="rpnow" onClick={() => setOffset(0)}>
            <Icon name="refresh" />Now
          </button>
        ) : null}

        {/* Three ways out, each looking like the thing it makes: a workbook,
            a plain file, a sheet of paper. */}
        <div className="rpacts">
          <button
            type="button"
            className="rpout xls"
            onClick={() => save('xlsx')}
            disabled={loading || !!saving}
            title="The laid-out workbook: colour, column widths, the XYRA mark, and a sheet each for the summary, the people, the partners and every demo"
          >
            <span className="rpoi">{saving === 'xlsx' ? <Spinner /> : <Icon name="sheet" />}</span>
            <span className="rpot"><b>Excel</b><small>Laid out, four sheets</small></span>
          </button>
          <button
            type="button"
            className="rpout csv"
            onClick={() => save('csv')}
            disabled={loading || !!saving}
            /* Named for what it is. A CSV is text with commas in it: no
               column widths, no colour, no mark. Called "opens anywhere" it
               was opened in Excel and judged against the workbook, which it
               can never look like. */
            title="The numbers as plain comma-separated text, for loading into another system. No formatting — use Excel or PDF for a report to read or send."
          >
            <span className="rpoi">{saving === 'csv' ? <Spinner /> : <Icon name="file" />}</span>
            <span className="rpot"><b>CSV</b><small>Raw data, no layout</small></span>
          </button>
          <button
            type="button"
            className="rpout pdf"
            onClick={() => save('pdf')}
            disabled={loading || !!saving}
            title="A laid-out document with the XYRA mast on every page: the figures, who came, who came back, the partners and every demo"
          >
            <span className="rpoi">{saving === 'pdf' ? <Spinner /> : <Icon name="file" />}</span>
            <span className="rpot"><b>PDF</b><small>Laid out for paper</small></span>
          </button>
          <button
            type="button"
            className="rpout prn"
            onClick={() => window.print()}
            disabled={loading}
            title="Print what is on this screen"
          >
            <span className="rpoi"><Icon name="printer" /></span>
            <span className="rpot"><b>Print</b><small>this screen</small></span>
          </button>
        </div>
      </div>

      {loading ? (
        <p className="muted utload"><Spinner /> Counting the demos…</p>
      ) : (
        <>
          {/* ------------------------------------------------ the numbers */}
          <div className="rptiles">
            {tiles.map(t => (
              /* A button, not a card: it opens something, and somebody on a
                 keyboard has to be able to reach it. */
              <TileCard key={t.k} t={t} onOpen={setTile} />
            ))}
          </div>

          {/* Straight under the figures it breaks down, because it is the
              first question anybody asks of them: of the ones that ran,
              how many were with people we already had. */}
          <Card className="rpwide rpsplitcard">
            <CardBody>
              <header className="rphead">
                <div>
                  <h4>Registered, by what happened</h4>
                  <p>
                    One row per person, so the two sides come to the {cur.people} who booked —
                    somebody who came more than once is counted once, under the best they
                    reached. Any figure opens the demos behind it.
                  </p>
                </div>
              </header>
              <div className="rpsides">
                {['reg', 'not'].map(side => (
                  <SplitPanel
                    key={side}
                    side={side}
                    rows={splitRows}
                    total={splitTotals[side]}
                    all={splitTotals.all}
                    onOpen={setTile}
                  />
                ))}
              </div>
            </CardBody>
          </Card>

          {cur.repeatPeople ? (
            <Card className="rpwide">
              <CardBody>
                <CameBack cur={cur} onOpen={() => setTile('repeat')} />
              </CardBody>
            </Card>
          ) : null}

          {/* ------------------------------------------------- the shape */}
          <div className="rpgrid">
            <Card className="rpwide">
              <CardBody>
                <header className="rphead">
                  <div>
                    <h4>{trend.title}</h4>
                    <p>Each column is one {period.grain === 'custom' ? 'slice' : period.grain}. The one in colour is the period on screen.</p>
                  </div>
                  <b className="rpbig">{cur.scheduled}</b>
                </header>
                <PeriodBars points={trend.points} />
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <header className="rphead">
                  <div>
                    <h4>What came of them</h4>
                    <p>{happened ? `${rate(cur.completed, happened)} of the demos that reached their slot were attended.` : 'Nothing has reached its slot yet.'}</p>
                  </div>
                </header>
                <RankBars
                  total={cur.scheduled}
                  empty="No demos in this period"
                  rows={[
                    { label: 'Completed', value: cur.completed },
                    { label: 'No-show', value: cur.noShow },
                    { label: 'Cancelled', value: cur.cancelled },
                    { label: 'Still upcoming', value: cur.upcoming }
                  ].filter(r => r.value)}
                />
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <header className="rphead">
                  <div>
                    <h4>Who booked them</h4>
                    <p>{cur.people} {cur.people === 1 ? 'person' : 'people'} booked {cur.scheduled} demo{cur.scheduled === 1 ? '' : 's'}.</p>
                  </div>
                </header>
                <RankBars
                  total={cur.scheduled}
                  empty="Nobody yet"
                  rows={[
                    { label: 'Registered', value: cur.matchedDemos },
                    { label: 'Registered manually', value: cur.manualDemos },
                    { label: 'Not registered', value: cur.unmatchedDemos }
                  ].filter(r => r.value)}
                />
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <header className="rphead">
                  <div>
                    <h4>How they were booked</h4>
                    <p>
                      {cur.byHand
                        ? `${cur.byHand} of ${cur.scheduled} never went through Calendly.`
                        : 'Every demo in this period came through Calendly.'}
                    </p>
                  </div>
                </header>
                <RankBars
                  total={cur.scheduled}
                  empty="No demos in this period"
                  rows={[
                    { label: 'Booked on Calendly', value: cur.fromCalendly },
                    { label: 'Added by hand', value: cur.byHand }
                  ].filter(r => r.value)}
                />
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <header className="rphead">
                  <div>
                    <h4>Partners and customers</h4>
                    <p>Taken from the meeting that was booked, not typed by hand.</p>
                  </div>
                </header>
                <SplitBar
                  empty="No demos in this period"
                  parts={[
                    { label: 'B2B partner sessions', value: cur.b2bDemos, color: B2B_COLOR },
                    { label: 'B2C customer demos', value: cur.b2cDemos, color: B2C_COLOR }
                  ]}
                />
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <header className="rphead">
                  <div>
                    <h4>Where the follow-ups got to</h4>
                    <p>Of the demos booked in this period.</p>
                  </div>
                </header>
                <RankBars
                  total={cur.scheduled}
                  empty="Nothing handed over yet"
                  rows={[
                    { label: 'Not started', value: cur.handed },
                    { label: 'Following up', value: cur.following },
                    { label: 'Closed', value: cur.closed },
                    { label: 'Not handed over', value: cur.notHanded }
                  ].filter(r => r.value)}
                />
              </CardBody>
            </Card>
          </div>

          {/* ------------------------------- the follow-ups, one by one */}
          {FOLLOW_SECTIONS.map(([kind, title, icon, tone, note, empty, third]) => (
            <FollowSection
              key={kind}
              kind={kind}
              title={title}
              icon={icon}
              tone={tone}
              note={note}
              empty={empty}
              third={third}
              list={follows[kind]}
              now={now}
            />
          ))}

          {/* ------------------------------------- what the demo changed */}
          <Card>
            <CardBody>
              <header className="rphead">
                <div>
                  <h4>What the demo changed</h4>
                  <p>
                    Where each person stood on the day they sat through the demo, against
                    where they stand now. Only demos that were attended — somebody who did
                    not turn up and registered later did not register because of this.
                  </p>
                </div>
                <span className="rpn">{shift.held} attended</span>
              </header>

              {shift.held ? (
                <>
                  {/*
                    * The counts, before the list that backs them up.
                    *
                    * Registered on the day and registered now sit on two bars
                    * of one scale rather than being reduced to a single
                    * "converted" figure: the gap between them is the answer,
                    * and one number hides which side it came from.
                    */}
                  <ShiftCompare shift={shift} />
                  {/*
                    * Every demo that was sat through, not only the ones that
                    * changed something.
                    *
                    * This listed the three that moved, which answered "who
                    * moved" and not the question the block is for: a demo
                    * that changed nothing is a result too, and a list of
                    * three under a tile reading 72 looks like a list that
                    * has lost sixty-nine rows.
                    *
                    * Ordered by what changed, then by when — the movement is
                    * what somebody is scanning for, and it is rare.
                    */}
                  {shift.all.length ? (
                    <div className="dstable">
                      <table>
                        <thead>
                          <tr>
                            <th>Who</th><th>Demo</th><th>Before the demo</th><th /><th>Now</th><th />
                          </tr>
                        </thead>
                        <tbody>
                          {shift.all
                            .slice()
                            .sort((a, b) => (b.moved - a.moved)
                              || ((b.planMoved ? 1 : 0) - (a.planMoved ? 1 : 0))
                              || new Date(b.booking.start) - new Date(a.booking.start))
                            .slice(0, 200)
                            .map(r => (
                              <tr
                                key={r.booking.id}
                                className={r.moved > 0 || r.planMoved ? 'dsup' : r.moved < 0 ? 'dsdown' : ''}
                                onClick={() => setSheet({ kind: 'demoShift', id: r.booking.id })}
                              >
                                <td>
                                  <div className="srwho">
                                    <Avatar name={r.client?.name || r.booking.invitee?.name || '?'} />
                                    <div>
                                      <b>{r.client?.name || r.booking.invitee?.name || 'Unknown'}</b>
                                      <small>{r.booking.invitee?.email || ''}</small>
                                    </div>
                                  </div>
                                </td>
                                <td>{stamp(r.booking.start)}</td>
                                {/* Standing and plan together, one column a side.
                                    Plan was a column of its own showing only
                                    today's, which left "before" with no plan at all
                                    and made the one name read as "the same either
                                    way". Each side now says what it knows and
                                    admits what it does not. */}
                                <td>
                                  <div className="dsside">
                                    <Pill tone={tone(r.before)}>{rungLabel(r.before)}</Pill>
                                    <small>
                                      {r.planBefore
                                        ? r.planBefore
                                        : r.before === 'none'
                                          ? <i>no plan</i>
                                          : <i title="Already a customer; the plan they were on that day was never recorded">not recorded</i>}
                                    </small>
                                  </div>
                                </td>
                                <td className="dsarrow"><Icon name="chev" /></td>
                                <td>
                                  <div className="dsside">
                                    <Pill tone={tone(r.after)}>{rungLabel(r.after)}</Pill>
                                    <small>
                                      {r.plan ? (r.planMoved ? <b>{r.plan}</b> : r.plan) : <i>no plan</i>}
                                    </small>
                                  </div>
                                </td>
                                <td className="dsgo"><Icon name="chev" /></td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                      {shift.all.length > 200
                        ? <p className="dsmore">and {shift.all.length - 200} more</p> : null}
                    </div>
                  ) : null}

                  {/*
                    * Said plainly, because a reader will assume the opposite.
                    *
                    * The session has an outcome field and a closed-as field and
                    * both are empty on nearly every row, so none of this comes
                    * from what anybody recorded after a demo — it is read off
                    * the client's own registration and payment dates. That is
                    * why a customer who upgraded reads as unchanged: which plan
                    * they were on that day is not kept anywhere.
                    */}
                  <p className="dsnote">
                    <Icon name="alert" />
                    <span>
                      Read from each client&rsquo;s registration and payment dates, and from
                      the plan recorded when the booking was matched. Closing a follow-up
                      asks what it closed as, and that answer is used where it is there —
                      {shift.said.length
                        ? ` ${shift.said.length} of these carry one.`
                        : ' none of these carry one yet.'}
                      {shift.unknownPlan.length ? (
                        <>
                          {' '}
                          <b>{shift.unknownPlan.length}</b> were already customers with no
                          record of the plan they were on that day, so an upgrade among
                          them cannot be seen — they read as unchanged.
                        </>
                      ) : null}
                    </span>
                  </p>
                </>
              ) : (
                <EmptyState icon="users" tone="g-slate" title="No demo was attended in this period">
                  Nothing to compare until somebody sits through one.
                </EmptyState>
              )}
            </CardBody>
          </Card>

          {/* --------------------------------------------- the B2B table */}
          <Card>
            <CardBody>
              <header className="rphead">
                <div>
                  <h4>B2B partners</h4>
                  <p>
                    {cur.partners
                      ? `${cur.partners} partner${cur.partners === 1 ? '' : 's'} ran ${cur.b2bDemos} session${cur.b2bDemos === 1 ? '' : 's'}${cur.repeatPartners ? ` — ${cur.repeatPartners} of them came back more than once.` : '.'}`
                      : 'No partner sessions in this period.'}
                  </p>
                </div>
              </header>

              {cur.partnerList.length ? (
                <div className="rptable">
                  <table>
                    <thead>
                      <tr>
                        <th aria-label="Open" />
                        <th>Partner</th>
                        <th className="num">Sessions</th>
                        <th className="num">Completed</th>
                        <th className="num">No-shows</th>
                        <th>Status</th>
                        <th>Last session</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cur.partnerList.map(p => (
                        <PartnerRow key={p.key} p={p} open={openPartner === p.key}
                          onToggle={() => setOpenPartner(openPartner === p.key ? null : p.key)}>
                          <td>
                            <div className="rpwho">
                              <Avatar name={p.name} />
                              <div>
                                <b>{p.name}</b>
                                <small>
                                  {p.code ? <span className="dbk-code">{p.code}</span> : null}
                                  {p.business || '—'}
                                </small>
                              </div>
                            </div>
                          </td>
                          <td className="num">
                            <b>{p.sessions}</b>
                            {p.sessions > 1 ? <em className="rpmulti">multiple</em> : null}
                          </td>
                          <td className="num">{p.completed}</td>
                          <td className="num">{p.noShow || '—'}</td>
                          <td>
                            <Pill
                              tone={REG_META[p.how][1]}
                              icon={REG_META[p.how][2] || undefined}
                              className="dbk-client"
                            >
                              {REG_META[p.how][0]}
                            </Pill>
                            {p.chasing ? <Pill tone="gr-blue" icon="flag" className="dbk-client">Under follow-up</Pill> : null}
                          </td>
                          <td>{p.last ? dayText(p.last) : '—'}</td>
                        </PartnerRow>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="cempty">No B2B partner booked a session in this period.</div>
              )}
            </CardBody>
          </Card>
        </>
      )}

      {tile ? (
        <TileDetail tile={tile} cur={cur} period={period} now={now} onClose={() => setTile('')} />
      ) : null}

      <p className="cvnote rpnote">
        Counted by when the demo was due to happen. Completed, no-show and
        cancelled are Calendly&rsquo;s; registered is the match to the Client
        Master.
        {' '}<b>All times are {TZ_LABEL}.</b>
      </p>
    </div>
  );
}
